import { useEffect, useState } from 'react';
import _ from 'lodash';
import { Alert, Form } from 'react-bootstrap';
import { Loading } from 'components/loading/index';
import { useQueryConstraintsValidations } from '../../../../hooks/queries';
import UserPermissionAlert from './Components/UserPermissionAlert';
import OrgConstraintAlert from './Components/OrgConstraintAlert';
import ProjectPermission from './Components/ProjectPermission';

import {
  defaultSwitchSelections,
  requiredPermissions,
  organizationViewerProps,
  projectCreatorProps,
  projectBrowserProps,
} from './permissionsConfig';

import {
  isAllowedToConfigure,
  isMissingUserPermission,
  isOrgConstraintSet,
} from './assertLib';

const RackspacePermissionsContent = ({
  active, index, result, stepResults, setCurrentStepResult,
}) => {
  const organizationDomain = _.get(stepResults[index - 1], ['googleOrganization', 'name']);
  const organizationDisplayName = _.get(stepResults[index - 1], ['googleOrganization', 'displayName']);
  const [permissionsToggle, setPermissionsToggle] = useState({ ...defaultSwitchSelections });
  const [initialConstraintsState, setInitialConstraintsState] = useState(false);
  const [initialNoConstraintsState, setInitialNoConstraintsState] = useState(false);

  const {
    error,
    isFetching,
    data: constraintsValidationResult,
  } = useQueryConstraintsValidations(active, organizationDomain, requiredPermissions);

  useEffect(() => {
    if (active && !initialNoConstraintsState && isFetching) {
      setInitialNoConstraintsState(true);
      setCurrentStepResult({
        roles: { ...defaultSwitchSelections },
        constraints: { permissionError: false, orgPolicyError: false },
        loading: true,
      });
    }
  }, [active, initialNoConstraintsState, isFetching, setCurrentStepResult]);

  if (isFetching) {
    return (<Loading centered />);
  }

  if (error) {
    return (
      <Alert variant="danger">
        <div>We could not complete your request.</div>
        <Alert.Link onClick={() => window.location.reload()}>Refresh</Alert.Link>
      </Alert>
    );
  }

  const missingUserPermission = isMissingUserPermission(constraintsValidationResult);
  const orgPolicyConstraintSet = isOrgConstraintSet(constraintsValidationResult);
  const allowedToConfigure = isAllowedToConfigure(constraintsValidationResult);

  const commonAlertProps = {
    initialConstraintsState,
    isFetching,
    organizationDisplayName,
    result,
    setCurrentStepResult,
    setInitialConstraintsState,
  };

  const commonPermissionProps = {
    active,
    allowedToConfigure,
    isFetching,
    permissionsToggle,
    result,
    setCurrentStepResult,
    setPermissionsToggle,
  };

  return (
    <div>
      <p>
        We recommend granting these limited roles to Rackspace on
        {' '}
        <strong>{ organizationDisplayName }</strong>
      </p>
      <p>
        These roles have been selected to minimize access, while providing us the ability to service your account efficiently.
      </p>
      <Form className="d-flex gap-8 flex-column mt-8 mb-8">
        <ProjectPermission
          {...commonPermissionProps}
          {...organizationViewerProps}
          isChecked={permissionsToggle['Organization Viewer']}
        />

        <ProjectPermission
          {...commonPermissionProps}
          {...projectCreatorProps}
          isChecked={permissionsToggle['Project Creator']}
        />

        <ProjectPermission
          {...commonPermissionProps}
          {...projectBrowserProps}
          isChecked={permissionsToggle['Project Browser']}
        />
      </Form>
      <UserPermissionAlert {...commonAlertProps} missingUserPermission={missingUserPermission} />
      <OrgConstraintAlert {...commonAlertProps} orgPolicyConstraintSet={orgPolicyConstraintSet} />
    </div>
  );
};

export default RackspacePermissionsContent;
